import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ServiceCard } from '@/components/service/ServiceCard';
import { useService } from '@/context/ServiceContext';
import { Medal as Award, Wrench, Clock, Phone } from '@phosphor-icons/react';
import './page-layout.css';
import './ServicePage.css';

export default function ServicePage() {
  const { serviceCatalog, loading } = useService();

  return (
    <div className="page">
      {/* Hero Section */}
      <section className="service-page__hero">
        <div className="page__container page__container--narrow page__centered">
          <h1 className="service-page__hero-title">Service center</h1>
          <p className="service-page__hero-lead">
            Professional car service in Amsterdam. Certified mechanics, original parts, and exceptional service.
          </p>
          <Link to="/service/appointment">
            <Button size="lg" className="service-page__btn--on-light">
              Schedule Service
            </Button>
          </Link>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="service-page__section service-page__section--surface">
        <div className="page__container page__container--narrow">
          <h2 className="service-page__title service-page__title--loose">Why choose us</h2>
          <div className="page__grid page__grid--3 page__grid--gap-lg">
            <div className="page__centered">
              <div className="service-page__badge">
                <Award size={32} />
              </div>
              <h3 className="service-page__step-title">Certified mechanics</h3>
              <p className="page__muted">
                Our technicians are factory-trained and certified to work on all major brands.
              </p>
            </div>
            <div className="page__centered">
              <div className="service-page__badge">
                <Wrench size={32} />
              </div>
              <h3 className="service-page__step-title">Original parts</h3>
              <p className="page__muted">
                We use only genuine OEM parts to ensure the best quality and longevity.
              </p>
            </div>
            <div className="page__centered">
              <div className="service-page__badge">
                <Clock size={32} />
              </div>
              <h3 className="service-page__step-title">Fast service</h3>
              <p className="page__muted">
                Most services completed the same day. We respect your time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Catalog */}
      <section className="service-page__section">
        <div className="page__container page__container--narrow">
          <h2 className="service-page__title">Our services</h2>
          <p className="service-page__lead">
            From routine maintenance to complex repairs, we've got you covered.
          </p>

          {loading ? (
            <div className="page__centered">
              <div className="service-page__spinner"></div>
              <p className="page__muted page__muted--sm">Loading services...</p>
            </div>
          ) : (
            <div className="page__grid page__grid--3">
              {serviceCatalog.map((service) => (
                <ServiceCard key={service.serviceId} {...service} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-page__section service-page__section--dark">
        <div className="page__container page__container--narrow page__centered">
          <h2 className="service-page__title service-page__title--plain">Ready to book?</h2>
          <p className="service-page__lead service-page__lead--hero">
            Schedule your service appointment online in just a few minutes.
          </p>
          <div className="service-page__actions">
            <Link to="/service/appointment">
              <Button size="lg" className="service-page__btn--accent">
                Schedule Now
              </Button>
            </Link>
            <a href="tel:+31201234567">
              <Button size="lg" variant="outline" className="service-page__btn--on-dark-outline">
                <Phone size={16} />
                Call Us: +31 20 123 4567
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Working Hours */}
      <section className="service-page__section service-page__section--muted">
        <div className="page__container page__container--narrow">
          <Card className="service-page__state">
            <h2 className="service-page__title service-page__title--sm">Service hours</h2>
            <div className="page__stack">
              <div className="service-page__row service-page__row--divided">
                <span className="page__strong">Monday - Friday</span>
                <span className="page__muted">9:00 AM - 6:00 PM</span>
              </div>
              <div className="service-page__row service-page__row--divided">
                <span className="page__strong">Saturday</span>
                <span className="page__muted">Closed</span>
              </div>
              <div className="service-page__row">
                <span className="page__strong">Sunday</span>
                <span className="page__muted">Closed</span>
              </div>
            </div>
            <p className="service-page__note">
              * Please book appointments at least 24 hours in advance
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
}
